/**
 * useKeyboardShortcuts — global keydown registry for Ask Argus.
 *
 * Provides:
 *   - `useKeyboardShortcut(key, handler, opts?)` — register a single shortcut.
 *   - `useGlobalShortcuts(handlers)` — register all Ask Argus shortcuts in one call.
 *   - `SHORTCUT_CATALOG` — typed array of every registered shortcut entry,
 *     consumed by KeyboardShortcutsHelp to auto-populate the dialog without
 *     manual duplication.
 *
 * Focus context rules (spec §5 Wave 3C):
 *   If focus is inside a text input / textarea / contenteditable, only
 *   `Escape` and `Cmd/Ctrl+Enter` fire.  All other shortcuts (j/k, ?,
 *   Cmd+K, Cmd+/) are suppressed so they never clobber typed text.
 *
 * Spec: docs/CHAT-UI-IMPROVEMENT-DESIGN.md §5 Wave 3C
 */

import { useEffect, useCallback } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface KeyboardShortcutEntry {
  /** Display label shown in the help dialog. */
  label: string;
  /** Grouping category in the help dialog. */
  category: 'Navigation' | 'Composer' | 'Thread';
  /** The primary key or character (e.g. 'k', '/', 'Enter', 'Escape', 'j'). */
  key: string;
  /** Whether the Cmd (Mac) / Ctrl (Windows/Linux) modifier is required. */
  mod?: boolean;
  /** Whether Shift is required in addition to mod. */
  shift?: boolean;
  /**
   * When true the shortcut ONLY fires when focus is inside a text input.
   * When false/undefined, it fires ONLY when focus is NOT in a text input
   * (unless it is an exception key like Esc or Cmd+Enter).
   */
  inputOnly?: boolean;
}

export interface UseKeyboardShortcutOptions {
  /**
   * When true, the shortcut is allowed to fire even when focus is inside
   * a text input / textarea / contenteditable (e.g. Cmd+Enter, Escape).
   * When false (default), the shortcut is suppressed in text inputs.
   */
  allowInInput?: boolean;
  /** Whether to call `event.preventDefault()` when the shortcut fires. Default true. */
  preventDefault?: boolean;
  /** Whether the handler is currently active. When false the handler is a no-op. */
  enabled?: boolean;
}

// ── Focus context helper ──────────────────────────────────────────────────────

/**
 * Returns true when the keyboard event originated from a text-entry element
 * (input, textarea, or contenteditable).  Used to suppress navigation
 * shortcuts so they do not clobber typing.
 */
export function isEditableTarget(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  if (!target) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea') return true;
  if (target.isContentEditable) return true;
  return false;
}

// ── Single-shortcut hook ──────────────────────────────────────────────────────

/**
 * Registers a global `keydown` listener for a specific keyboard shortcut.
 *
 * @param key         The `event.key` value to match (case-insensitive for letters).
 * @param handler     Callback invoked when the shortcut fires.
 * @param opts        Fine-grained control over input suppression and enabled state.
 */
export function useKeyboardShortcut(
  key: string,
  handler: (event: KeyboardEvent) => void,
  opts: UseKeyboardShortcutOptions = {},
): void {
  const { allowInInput = false, preventDefault = true, enabled = true } = opts;

  const stableHandler = useCallback(handler, [handler]);

  useEffect(() => {
    if (!enabled) return;

    const listener = (event: KeyboardEvent) => {
      // Suppress in text inputs unless explicitly allowed.
      if (!allowInInput && isEditableTarget(event)) return;

      const matchesKey =
        event.key.toLowerCase() === key.toLowerCase() ||
        event.key === key;

      if (!matchesKey) return;

      if (preventDefault) event.preventDefault();
      stableHandler(event);
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [key, stableHandler, allowInInput, preventDefault, enabled]);
}

// ── Shortcut catalog ─────────────────────────────────────────────────────────

/**
 * Static catalog of every keyboard shortcut in Ask Argus.
 * The KeyboardShortcutsHelp dialog reads this to auto-populate its list.
 * Adding a shortcut here + wiring the handler is all that is required.
 */
export const SHORTCUT_CATALOG: KeyboardShortcutEntry[] = [
  // ── Navigation ────────────────────────────────────────────────────────────
  {
    label: 'Open conversation search',
    category: 'Navigation',
    key: 'k',
    mod: true,
  },
  {
    label: 'Toggle left panel (conversations)',
    category: 'Navigation',
    key: 'b',
    mod: true,
  },
  {
    label: 'Toggle right panel (context)',
    category: 'Navigation',
    key: 'b',
    mod: true,
    shift: true,
  },

  // ── Composer ──────────────────────────────────────────────────────────────
  {
    label: 'Send message',
    category: 'Composer',
    key: 'Enter',
  },
  {
    label: 'New line in composer',
    category: 'Composer',
    key: 'Enter',
    shift: true,
  },
  {
    label: 'Send message (alternative)',
    category: 'Composer',
    key: 'Enter',
    mod: true,
    inputOnly: true,
  },
  {
    label: 'Open slash commands menu',
    category: 'Composer',
    key: '/',
  },
  {
    label: 'Trigger slash menu via shortcut',
    category: 'Composer',
    key: '/',
    mod: true,
  },
  {
    label: 'Mention an agent or tool (@)',
    category: 'Composer',
    key: '@',
  },
  {
    label: 'Close menus / cancel stream',
    category: 'Composer',
    key: 'Escape',
    inputOnly: true,
  },

  // ── Thread ────────────────────────────────────────────────────────────────
  {
    label: 'Focus next message',
    category: 'Thread',
    key: 'j',
  },
  {
    label: 'Focus previous message',
    category: 'Thread',
    key: 'k',
  },
  {
    label: 'Open keyboard shortcuts help',
    category: 'Thread',
    key: '?',
  },
];

// ── Global shortcut options shape ─────────────────────────────────────────────

export interface GlobalShortcutHandlers {
  /** Cmd/Ctrl+K: open search palette. */
  onOpenSearch: () => void;
  /** Cmd/Ctrl+Enter: submit composer (allowed in input). */
  onComposerSubmit: () => void;
  /** Escape: abort stream when streaming; otherwise no-op at global level. */
  onEscapeStream: () => void;
  /** Cmd/Ctrl+/: trigger slash menu. */
  onSlashMenu: () => void;
  /** ?: open keyboard shortcuts help dialog. */
  onOpenHelp: () => void;
  /** Whether the chat stream is currently active (gates Esc behaviour). */
  isStreaming: boolean;
}

/**
 * `useGlobalShortcuts` — mount all Ask Argus keyboard shortcuts in one call.
 *
 * j/k message navigation is handled directly in ChatMessages (needs access
 * to the virtualizer context) and is NOT included here.
 *
 * Called from `routes/ask-argus/index.tsx`.
 */
export function useGlobalShortcuts({
  onOpenSearch,
  onComposerSubmit,
  onEscapeStream,
  onSlashMenu,
  onOpenHelp,
  isStreaming,
}: GlobalShortcutHandlers): void {
  // ── Cmd/Ctrl+K — open search palette ──────────────────────────────────────
  useKeyboardShortcut(
    'k',
    (e) => {
      if (e.metaKey || e.ctrlKey) onOpenSearch();
    },
    { allowInInput: false, preventDefault: true },
  );

  // ── Cmd/Ctrl+Enter — submit composer (fires even inside textarea) ──────────
  useKeyboardShortcut(
    'Enter',
    (e) => {
      if (e.metaKey || e.ctrlKey) onComposerSubmit();
    },
    { allowInInput: true, preventDefault: true },
  );

  // ── Escape — abort stream (only when streaming; no-op otherwise) ──────────
  useKeyboardShortcut(
    'Escape',
    (_e) => {
      if (isStreaming) onEscapeStream();
      // When NOT streaming, Esc is handled by individual menus / dialogs
      // via their own close handlers. We do not capture it globally.
    },
    { allowInInput: true, preventDefault: false },
  );

  // ── Cmd/Ctrl+/ — open slash menu in composer ──────────────────────────────
  useKeyboardShortcut(
    '/',
    (e) => {
      if (e.metaKey || e.ctrlKey) onSlashMenu();
    },
    { allowInInput: false, preventDefault: true },
  );

  // ── ? — open help dialog (without modifier, not in inputs) ────────────────
  useKeyboardShortcut(
    '?',
    (_e) => {
      onOpenHelp();
    },
    { allowInInput: false, preventDefault: true },
  );
}
